// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as React from 'react';
import { useState, type ErrorInfo, type ReactNode, Suspense, lazy } from 'react';
import { MusicPlayerProvider } from './contexts/MusicPlayerContext';
import ErrorBoundary from './components/ErrorBoundary';
import { ErrorSeverity } from './types/error-logs';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import PageTracker from './components/PageTracker';
import { AuthProvider, useAuth } from './lib/auth';
import { ThemeProvider } from './context/ThemeContext';
import { GDPRProvider } from './context/GDPRContext';
import { GDPRConsentModal } from './components/modals/GDPRConsentModal';
import { errorReportingService } from './lib/services/error-reporting-service';
import { ensureStorageBuckets } from './lib/services/supabase-bucket-setup';

// Critical components - load immediately
import Landing from './pages/Landing/Landing';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import Feed from './components/home/Feed';
import Navbar from './components/Navbar';
import LoadingSpinner from './components/LoadingSpinner';

// Lazy load heavy components for better performance
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/auth/Verify'));
const Messages = lazy(() => import('./pages/Message/messageindex'));
const Notifications = lazy(() => import('./pages/settings/Notifications'));
const NotificationsPage = lazy(() => import('./pages/Notifications/NotificationsPage'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const EditProfile = lazy(() => import('./pages/settings/EditProfile'));
const Settings = lazy(() => import('./pages/settings/index'));
const Verify = lazy(() => import('./pages/settings/Verify'));
const AccountStatus = lazy(() => import('./pages/settings/AccountStatus'));
const Search = lazy(() => import('./pages/Search/searchindex'));
const Trending = lazy(() => import('./pages/Trending/Trending'));
const Pages = lazy(() => import('./pages/Pages/pagesindex'));
const PageDetail = lazy(() => import('./pages/Pages/PageDetail'));
const MultiMediaDemo = lazy(() => import('./pages/Demos/MultiMediaDemo'));
const MultiSongDemo = lazy(() => import('./pages/Demos/MultiSongDemo'));
const Activity = lazy(() => import('./pages/Activity/Activity'));
const Music = lazy(() => import('./pages/Music/musicindex'));
const ActivitySummaryTest = lazy(() => import('./pages/Activity/ActivitySummaryTest'));
const Bookmarks = lazy(() => import('./pages/Bookmarks/bookmarks'));
const NetworkStatus = lazy(() => import('./components/pwa/NetworkStatus'));
const UpdateNotification = lazy(() => import('./components/pwa/UpdateNotification'));
const Locations = lazy(() => import('./pages/Locations/Locations'));
const ActivitySummaryProvider = lazy(() => import('./components/ActivitySummaryProvider'));
const PushNotificationManager = lazy(() => import('./components/PushNotificationManager'));
const PushNotificationDebug = lazy(() => import('./components/debug/PushNotificationDebug'));

// Settings pages
const AffiliatePage = lazy(() => import('./pages/settings/affiliate'));
const PasswordAndSecurity = lazy(() => import('./pages/settings/PasswordAndSecurity'));
const PrivacySettings = lazy(() => import('./pages/settings/Privacy'));
const Messaging = lazy(() => import('./pages/settings/Messaging'));
const AdvertiseSettings = lazy(() => import('./pages/settings/AdvertiseSettings'));
const BoostSettings = lazy(() => import('./pages/settings/BoostSettings'));
const Language = lazy(() => import('./pages/settings/Language'));
const Appearance = lazy(() => import('./pages/settings/Appearance'));
const Analytics = lazy(() => import('./pages/settings/Analytics'));
const StorySettingsPage = lazy(() => import('./pages/settings/StorySettingsPage'));
const DraftsPage = lazy(() => import('./pages/settings/DraftsPage'));

// Legal pages
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));
const CreatorGuidelines = lazy(() => import('./pages/legal/CreatorGuidelines'));
const CopyrightPolicy = lazy(() => import('./pages/legal/CopyrightPolicy'));
const CreatorsHub = lazy(() => import('./pages/CreatorsHub/creatorshubindex'));

// Company pages
const About = lazy(() => import('./pages/company/About'));
const Careers = lazy(() => import('./pages/company/Careers'));
const JobPositions = lazy(() => import('./pages/company/JobPositions'));
const JobApplication = lazy(() => import('./pages/company/JobApplication'));
const Blog = lazy(() => import('./pages/company/Blog'));
const Article = lazy(() => import('./pages/company/Article'));

// Support pages
const HelpCenter = lazy(() => import('./pages/support/HelpCenter'));
const Contact = lazy(() => import('./pages/support/Contact'));
const Community = lazy(() => import('./pages/CreatorsHub/Community'));

/**
 * Restricts access to child components, allowing only authenticated users to view them.
 *
 * While authentication status is loading, displays a centered spinner. If authentication is verified and no user is present, redirects to the login page, preserving the original location in navigation state. Otherwise, renders the provided children.
 *
 * @param children - The components to render if the user is authenticated.
 */
function PrivateRoute({ children }: { children: ReactNode }) {
  const { user, initialized } = useAuth();
  const location = useLocation();

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 dark:border-white"></div>
      </div>
    );
  }

  // Only redirect if auth state is fully initialized and user is not authenticated
  if (initialized && !user) {
    console.log('PrivateRoute redirecting to /login, no authenticated user after initialization');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children, publicPath }: { children: ReactNode; publicPath: string; }) {
  const { user, initialized } = useAuth();
  const location = useLocation();

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 dark:border-white"></div>
      </div>
    );
  }

  // If user is authenticated AND they are currently trying to access THIS specific public path,
  // redirect them to a default authenticated page (e.g., /feed).
  if (initialized && user && location.pathname === publicPath) {
    const from = location.state?.from?.pathname || '/feed';
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}

/**
 * Defines the main routing and layout structure for the application, including authentication-gated and public routes.
 *
 * @remarks
 * Manages UI state for the navbar collapse. Handles both authenticated and unauthenticated routes, rendering the appropriate layout and components for each path. Protected routes are wrapped with authentication checks, and unmatched routes redirect to the landing page.
 */
function AppContent() {
  const [isNavbarCollapsed, setIsNavbarCollapsed] = useState(false);

  return (
    <MusicPlayerProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <PageTracker />
        <ScrollToTop />
        <PushNotificationManager />
        <Routes>
          <Route path="/" element={<PublicRoute publicPath="/"><Landing /></PublicRoute>} />
          <Route path="/login" element={<PublicRoute publicPath="/login"><Login /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute publicPath="/signup"><Signup /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute publicPath="/forgot-password"><Suspense fallback={<LoadingSpinner />}><ForgotPassword /></Suspense></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute publicPath="/reset-password"><Suspense fallback={<LoadingSpinner />}><ResetPassword /></Suspense></PublicRoute>} />
          <Route path="/verify" element={<PublicRoute publicPath="/verify"><Suspense fallback={<LoadingSpinner />}><VerifyEmail /></Suspense></PublicRoute>} />

          {/* Admin Routes Removed */}

          {/* Protected Routes */}
          <Route
            path="/home"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Feed />
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          {/* Keep /feed for backward compatibility */}
          <Route
            path="/feed"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Feed />
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/search"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Search /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />


          <Route
            path="/message"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                <Navbar collapsed={true} />
                <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                  <Suspense fallback={<LoadingSpinner />}><Messages /></Suspense>
                </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><NotificationsPage /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/trending"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Trending /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />


          {/* Music route */}
          <Route
            path="/music"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Music /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/pages"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Pages /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/pages/:pageId"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><PageDetail /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/multi-song-demo"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><MultiSongDemo /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}
                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Settings /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/password-security"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}
                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><PasswordAndSecurity /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/privacy"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}
                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><PrivacySettings /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/notifications"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}
                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Notifications /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />


          <Route
            path="/settings/affiliate"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}
                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><AffiliatePage /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings/advertise"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><AdvertiseSettings /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings/boost"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><BoostSettings /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/language"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Language /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/appearance"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Appearance /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/messaging"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}
                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Messaging /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/story"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}
                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><StorySettingsPage /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/drafts"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}
                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><DraftsPage /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />
          
          {/* Test Routes */}
          {/* Storage test route removed */}

          <Route
            path="/settings/account-status"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}
                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><AccountStatus /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/analytics"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}
                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Analytics /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/edit-profile"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}
                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><EditProfile /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/verify"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}
                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Verify /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Notifications /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/activity"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar collapsed={true} />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Activity /></Suspense>
                  </main>
                </div>
              </PrivateRoute>
            }
          />

          {/* TODO: Reels feature is not yet implemented */}
          {/* <Route
          path="/reels"
          element={
            <PrivateRoute>
              <div className="flex min-h-screen bg-black">
                <div className="hidden md:block">
                  <Navbar collapsed={true}  />
                </div>
                <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0">
                  <Reels />
                </main>
                
              </div>
            </PrivateRoute>
          }
        /> */}

          {/* Other user's profile */}
          <Route
            path="/profile/:userId"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={true}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-16 mb-16 md:mb-0 px-2 sm:px-4" data-component-name="AppContent">
                    <Suspense fallback={<LoadingSpinner />}><Profile /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/profile/edit"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><EditProfile /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Settings /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/account-status"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><AccountStatus /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/verify"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Verify /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/affiliate"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><AffiliatePage /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/advertise"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Settings /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/settings/boost"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Settings /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/affiliate"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Settings /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/analytics"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Analytics /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/bookmarks"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Bookmarks /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/locations/:location"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><Locations /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          <Route
            path="/activity-summary-test"
            element={
              <PrivateRoute>
                <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                  <Navbar
                    collapsed={isNavbarCollapsed}

                    _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                  />
                  <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                    <Suspense fallback={<LoadingSpinner />}><ActivitySummaryTest /></Suspense>
                  </main>

                </div>
              </PrivateRoute>
            }
          />

          {/* Additional routes for direct access */}

          <Route path="/multi-media" element={<MultiMediaDemo />} />

          {/* Debug routes - development only */}
          {process.env.NODE_ENV === 'development' && (
            <Route 
              path="/push-notification-debug" 
              element={
                <PrivateRoute>
                  <div className="flex min-h-screen bg-gray-50 dark:bg-black">
                    <Navbar
                      collapsed={isNavbarCollapsed}
                      _onToggleCollapse={() => setIsNavbarCollapsed(prev => !prev)}
                    />
                    <main className="flex-1 ml-0 md:ml-64 mb-16 md:mb-0 px-2 sm:px-4">
                      <Suspense fallback={<LoadingSpinner />}><PushNotificationDebug /></Suspense>
                    </main>
                  </div>
                </PrivateRoute>
              } 
            />
          )}

          {/* Public Pages - Legal Pages */}
          <Route path="/legal/terms-of-service" element={<TermsOfService />} />
          <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/legal/cookie-policy" element={<CookiePolicy />} />
          <Route path="/legal/creator-guidelines" element={<CreatorGuidelines />} />
          <Route path="/legal/copyright-policy" element={<CopyrightPolicy />} />
          <Route path="/creators-hub" element={<CreatorsHub />} />
          <Route path="/creators/terms" element={<CreatorsHub defaultTab="legal" defaultLegalTab="terms" />} />

          {/* Public Pages - Company Pages */}
          <Route path="/company/about" element={<About />} />
          <Route path="/company/careers" element={<Careers />} />
          <Route path="/company/jobs/:id" element={<JobPositions />} />
          <Route path="/company/apply/:id" element={<JobApplication />} />
          <Route path="/company/blog" element={<Blog />} />
          <Route path="/company/blog/article/:id" element={<Article />} />

          {/* Public Pages - Support Pages */}
          <Route path="/support/help-center" element={<HelpCenter />} />
          <Route path="/support/contact" element={<Contact />} />
          <Route path="/community" element={<Community />} />

          {/* Catch-all route - must be last */}
          <Route
            path="*"
            element={
              <Navigate to="/" replace />
            }
          />
        </Routes>
      </Router>
    </MusicPlayerProvider>
  );
}

/**
 * Initializes and renders the root of the application with global error handling, theming, and authentication context.
 *
 * Displays a loading spinner during initialization, an error screen if initialization fails, and wraps the main content in layered error boundaries for robust error reporting and recovery.
 *
 * @returns The root React element for the application, including error boundaries, theme provider, authentication provider, and main content.
 */
function App() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);

  React.useEffect(() => {
    const initApp = async () => {
      try {
        // Fast initialization - only critical services
        await errorReportingService.setupGlobalErrorHandlers();
        console.log('Error reporting service initialized');
        
        // Set initialized immediately for faster loading
        setIsInitialized(true);
        
        // Initialize Supabase storage buckets in background (non-blocking)
        setTimeout(async () => {
          try {
            const bucketsResult = await ensureStorageBuckets();
            if (bucketsResult.success) {
              if (bucketsResult.created.length > 0) {
                console.log('Created Supabase storage buckets:', bucketsResult.created);
              }
              if (bucketsResult.existing.length > 0) {
                console.log('Using existing Supabase storage buckets:', bucketsResult.existing);
              }
            } else {
              console.warn('Failed to initialize some Supabase storage buckets:', bucketsResult.error);
              // Continue anyway - we'll handle bucket errors during upload attempts
            }
          } catch (bucketError) {
            console.warn('Background bucket initialization failed:', bucketError);
          }
        }, 100); // Initialize buckets after app loads
        
      } catch (err) {
        console.error('Failed to initialize error reporting:', err);
        setInitError(err instanceof Error ? err : new Error('Failed to initialize'));
        setIsInitialized(true);
      }
    };
    initApp();
  }, []);

  // Apply iOS status bar fix class to body
  React.useEffect(() => {
    // Check if the device is iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

    if (isIOS) {
      document.body.classList.add('ios-status-bar-fix');
    }

    return () => {
      if (isIOS) {
        document.body.classList.remove('ios-status-bar-fix');
      }
    };
  }, []);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 dark:border-white"></div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-4">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Initialization Error</h1>
          <p className="text-gray-700 dark:text-gray-300 mb-4">{initError.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    console.error('App Error:', error, errorInfo);
    errorReportingService.captureError(error, ErrorSeverity.ERROR, {
      component: 'App',
      componentStack: errorInfo.componentStack
    });
  };

  return (
    <ErrorBoundary
      componentName="App"
      onError={handleError}
      fallback={(error: Error, resetError: () => void) => (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center p-4">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <p className="text-gray-700 dark:text-gray-300 mb-4">{error.message}</p>
            <div className="space-x-4">
              <button
                onClick={resetError}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Reload App
              </button>
            </div>
          </div>
        </div>
      )}
    >
      <ThemeProvider>
        <AuthProvider>
          <GDPRProvider>
            <ErrorBoundary
              componentName="Network"
              onError={handleError}
            >
              <Suspense fallback={<LoadingSpinner />}><NetworkStatus /></Suspense>
            </ErrorBoundary>
            <ErrorBoundary
              componentName="UpdateNotification"
              onError={handleError}
            >
              <Suspense fallback={<LoadingSpinner />}><UpdateNotification /></Suspense>
            </ErrorBoundary>
            <ErrorBoundary
              componentName="GDPR"
              onError={handleError}
            >
              <GDPRConsentModal />
            </ErrorBoundary>
            {/* Debug logger removed */}
            <ErrorBoundary
              componentName="Content"
              onError={handleError}
            >
              <Suspense fallback={<LoadingSpinner />}><ActivitySummaryProvider><AppContent /></ActivitySummaryProvider></Suspense>
            </ErrorBoundary>
          </GDPRProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
